import React, { useMemo } from 'react';
import { Box } from '@televet/kibble-ui';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import HasFeature from '../HasFeature';
import TrupanionCheckout from '../SidePanel/components/InvoicingPanel/components/TrupanionCheckout/TrupanionCheckout';
import { SpeciesType, useGetSidePanelPetParentProfileQuery } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { differenceInYears, parseISO } from 'date-fns';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';

interface TrupanionProfileCheckProps {
  clinicPetParentId: string;
}

const TrupanionProfileCheck = ({ clinicPetParentId }: TrupanionProfileCheckProps): JSX.Element => {
  const { isFeatureEnabled } = useFeatureFlag();
  const { currentClinicId } = useClinicUser();
  const { data } = useGetSidePanelPetParentProfileQuery({
    variables: { clinicId: currentClinicId || '', clinicPetParentId },
    skip: !currentClinicId || !clinicPetParentId || !isFeatureEnabled(FeatureFlagName.TrupanionDigitalCheckout),
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });
  const clinicPetParent = data?.clinicPetParent;

  /**
   * Before we render the TrupanionCheckout component and make the request to Trupanion's API,
   * here we check if any of the client's pets meets the following criteria:
   *  1. Species is canine or feline
   *  2. Age is under 3 years old
   *
   * Potential future optimization: Check for a recent appointment of type matching the Trupanion type(s)
   */
  const isPotentiallyEligibleForTrupanionExamDayOffer = useMemo(() => {
    return clinicPetParent?.pets.some((pet) => {
      const isDogOrCat = [SpeciesType.Canine, SpeciesType.Feline].includes(pet.speciesType);
      const isUnder3Years = differenceInYears(new Date(), parseISO(pet.dateOfBirth)) < 3;
      return isDogOrCat && isUnder3Years;
    });
  }, [clinicPetParent?.pets]);

  return (
    <HasFeature name={FeatureFlagName.TrupanionDigitalCheckout}>
      {isPotentiallyEligibleForTrupanionExamDayOffer && (
        <Box pb={2}>
          <TrupanionCheckout clinicPetParentId={clinicPetParentId || ''} showOnlyWhenEligible />
        </Box>
      )}
    </HasFeature>
  );
};

export default TrupanionProfileCheck;
